import { Vector3, Quaternion, Euler } from "three";
import { AnimationDirection, easeInOutQuart } from "@rendering/vortex-core";
import { mirror, preserve } from "../common/pathHelper";
const DEGREES_TO_RADIANS = 0.0174533;
export class FoldingAnimation {
  constructor(product, config, options) {
    Object.defineProperty(this, "completionStatus", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: false
    });
    Object.defineProperty(this, "activeStatus", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: false
    });
    Object.defineProperty(this, "canceledStatus", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: false
    });
    Object.defineProperty(this, "elapsedTime", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: 0
    });
    Object.defineProperty(this, "duration", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: void 0
    });
    Object.defineProperty(this, "direction", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: void 0
    });
    Object.defineProperty(this, "product", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: void 0
    });
    Object.defineProperty(this, "config", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: void 0
    });
    Object.defineProperty(this, "primarySectionSet", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: void 0
    });
    Object.defineProperty(this, "foldStates", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: void 0
    });
    Object.defineProperty(this, "physicalSize", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: void 0
    });
    Object.defineProperty(this, "resolveAnimation", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: void 0
    });
    this.product = product;
    this.config = config;
    this.primarySectionSet = config.sectionSets[0];
    this.physicalSize = product.getPhysicalSize();
    this.foldStates = this.generateFoldStates(options.sequenceId, config);
    this.duration = options.duration;
    this.direction = options.direction;
  }
  get isComplete() {
    return this.completionStatus;
  }
  get isActive() {
    return this.activeStatus;
  }
  get tags() {
    return [];
  }
  get isCanceled() {
    return this.canceledStatus;
  }
  resetMeshState() {
    this.activeStatus = false;
    this.interpolateValues(this.duration);
    this.resolveAnimation && this.resolveAnimation();
  }
  update(delta) {
    if (this.isActive && this.elapsedTime < this.duration) {
      this.interpolateValues(this.elapsedTime);
      this.elapsedTime += delta;
    }
    if (this.elapsedTime >= this.duration && this.isActive) {
      this.interpolateValues(this.duration);
      this.resolveAnimation && this.resolveAnimation();
      this.activeStatus = false;
    }
  }
  start() {
    this.activeStatus = true;
    return new Promise(resolve => {
      this.resolveAnimation = resolve;
    });
  }
  resume() {
    if (this.resolveAnimation !== undefined) {
      this.activeStatus = true;
    }
  }
  stop() {
    this.activeStatus = false;
  }
  cancel() {
    this.activeStatus = false;
    this.canceledStatus = true;
    this.resetMeshState();
    if (this.resolveAnimation) {
      this.resolveAnimation();
    }
  }
  getFoldSequence(id) {
    for (let i = 0; i < this.primarySectionSet.foldSequences.length; i++) {
      if (this.primarySectionSet.foldSequences[i].id === id) {
        return this.primarySectionSet.foldSequences[i];
      }
    }
    throw new Error("Fold sequence '".concat(id, "' is not an available in the NFold Config!"));
  }
  getFold(id) {
    for (let i = 0; i < this.primarySectionSet.folds.length; i++) {
      if (this.primarySectionSet.folds[i].id === id) {
        return this.primarySectionSet.folds[i];
      }
    }
    throw new Error("Fold '".concat(id, "' is not an available in the NFold Config!"));
  }
  // Finds the last operation to occur for a specific fold id (max ending percentage)
  getLastOperationById(foldId, sequence) {
    let maxEndingRatio = 0;
    let lastOperation;
    for (const foldOperation of sequence.operations) {
      if (foldOperation.foldId === foldId && foldOperation.endRatio > maxEndingRatio) {
        lastOperation = foldOperation;
        maxEndingRatio = foldOperation.endRatio;
      }
    }
    if (lastOperation === undefined) {
      throw new Error("Fold operation '".concat(foldId, "' is not an available in the fold sequence '").concat(sequence.id, "'!"));
    }
    return lastOperation;
  }
  generateFoldStates(state,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _config) {
    const foldStates = {};
    const sequence = this.getFoldSequence(state);
    // The sequence has a dependency on an existing state for fold values
    if (sequence.dependencyId) {
      const parentSequence = this.getFoldSequence(sequence.dependencyId);
      const groupedFoldOperations = this.groupOperationsByFold(sequence.operations);
      Object.keys(groupedFoldOperations).forEach(foldId => {
        // Get the fold for this operation group
        const fold = this.getFold(foldId);
        foldStates[fold.childSectionId] = [];
        const foldOperations = groupedFoldOperations[foldId];
        for (let i = 0; i < foldOperations.length; i++) {
          let parentOperation;
          // If this is the first operation in the sequence, its parent is from the parent sequence
          if (i === 0) {
            parentOperation = this.getLastOperationById(foldId, parentSequence);
          } else {
            // Otherwise the parent is the previous fold in the current sequence
            parentOperation = foldOperations[i - 1];
          }
          // Set the fold state of the child section (effected section) for this fold
          foldStates[fold.childSectionId].push({
            fold,
            startRatio: foldOperations[i].startRatio,
            endRatio: foldOperations[i].endRatio,
            startAngle: parentOperation.angle,
            endAngle: foldOperations[i].angle
          });
        }
      });
    } else {
      // Otherwise assume the 'open' state (all base operations at 0)
      const groupedFoldOperations = this.groupOperationsByFold(sequence.operations);
      Object.keys(groupedFoldOperations).forEach(foldId => {
        // Get the fold for this operation group
        const fold = this.getFold(foldId);
        foldStates[fold.childSectionId] = [];
        const foldOperations = groupedFoldOperations[foldId];
        for (let i = 0; i < foldOperations.length; i++) {
          // The parent angle is 0 to start for the first operation
          let parentAngle = 0;
          // If multiple fold operations exist in the current sequence, the parentAngle should the previous fold angle
          if (i > 0) {
            parentAngle = foldOperations[i - 1].angle;
          }
          // Set the fold state of the child section (effected section) for this fold
          foldStates[fold.childSectionId].push({
            fold,
            startRatio: foldOperations[i].startRatio,
            endRatio: foldOperations[i].endRatio,
            startAngle: parentAngle,
            endAngle: foldOperations[i].angle
          });
        }
      });
    }
    return foldStates;
  }
  // Group all fold operations by their fold id
  groupOperationsByFold(foldOperations) {
    const groupedOperations = {};
    for (const foldOperation of foldOperations) {
      if (!groupedOperations[foldOperation.foldId]) {
        groupedOperations[foldOperation.foldId] = [foldOperation];
      } else {
        groupedOperations[foldOperation.foldId].push(foldOperation);
      }
    }
    return groupedOperations;
  }
  interpolateValues(time) {
    // Compute the animation ratio based on direction
    const animationRatio = this.direction === AnimationDirection.forward ? time / this.duration : 1.0 - time / this.duration;
    const sectionGroupMap = this.product.getSectionGroupMapping();
    // Animate each section
    Object.keys(sectionGroupMap).forEach(sectionId => {
      // The section may not have any operations
      if (this.foldStates[sectionId]) {
        const foldStates = this.foldStates[sectionId];
        // Run all fold states for the section
        for (let i = 0; i < foldStates.length; i++) {
          const foldState = foldStates[i];
          // Clamp the animation to its starting state
          if (animationRatio <= foldState.startRatio) {
            if (i === 0) {
              this.foldSection(sectionId, 0, foldState, sectionGroupMap);
            }
            // Clamp the animation to it's ending state
          } else if (animationRatio >= foldState.endRatio) {
            if (i === foldStates.length - 1) {
              this.foldSection(sectionId, 1, foldState, sectionGroupMap);
            }
            // Interpolate between the starting and ending ratios
          } else {
            const relativeTime = animationRatio - foldState.startRatio;
            const relativeDuration = foldState.endRatio - foldState.startRatio;
            // Interpolate between 0-1 based on relative ratios
            const interpolatedPercent = easeInOutQuart(relativeTime, 0, 1, relativeDuration);
            this.foldSection(sectionId, interpolatedPercent, foldState, sectionGroupMap);
          }
        }
      }
    });
  }
  foldSection(sectionId, animationRatio, foldState, sectionGroupMap) {
    const foldAngle = -DEGREES_TO_RADIANS * ((foldState.endAngle - foldState.startAngle) * animationRatio + foldState.startAngle);
    // Translate the fold line into coordinates with 0,0 being the bottom left
    const foldLine = {
      x0: preserve(foldState.fold.path.primary[0].x, this.physicalSize.width),
      y0: mirror(foldState.fold.path.primary[0].y, this.physicalSize.height),
      x1: preserve(foldState.fold.path.primary[1].x, this.physicalSize.width),
      y1: mirror(foldState.fold.path.primary[1].y, this.physicalSize.height)
    };
    // Reset group to initial state
    sectionGroupMap[sectionId].rotation.set(0, 0, 0);
    sectionGroupMap[sectionId].position.set(0, 0, 0);
    const foldVector = new Vector3(foldLine.x1, foldLine.y1, this.primarySectionSet.thickness);
    const foldAxis = new Vector3(foldLine.x1 - foldLine.x0, foldLine.y1 - foldLine.y0).normalize();
    const rotationPoint = this.rotateVector(foldVector, new Vector3(), new Euler());
    // Apply new position / rotation state
    this.applyRotation(sectionGroupMap[sectionId], rotationPoint, foldAngle, foldAxis);
  }
  rotateVector(edge, pivot, rotation) {
    let result = edge.sub(pivot);
    result = result.applyEuler(rotation);
    result = result.add(pivot);
    return result;
  }
  applyRotation(section, rotationPoint, rotationAngle, rotationAxis) {
    const quaternion = new Quaternion();
    quaternion.setFromAxisAngle(rotationAxis, rotationAngle);
    section.quaternion.multiplyQuaternions(quaternion, section.quaternion);
    section.position.sub(rotationPoint);
    section.position.applyQuaternion(quaternion);
    section.position.add(rotationPoint);
    return section;
  }
}